// TfAddToList component - React implementation of Angular tfAddToList directive
// Provides dropdown interface for adding products to shared lists

import React, { useState, useEffect, useRef } from "react";
import sharedListApi from "../services/sharedListApi";
import useAuth from "../hooks/useAuth";
import { CoreButton, CoreLoader } from "./komodo";
import "./TfAddToList.scss";

const TfAddToList = ({ label, product, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [lists, setLists] = useState([]);
  const [message, setMessage] = useState(null);
  const [name, setName] = useState("");
  const containerRef = useRef(null);
  const { userId } = useAuth();

  // Localization helper - matches Angular getString function
  const getString = (key) => {
    const strings = {
      "new_list.create_first": "Create your first list",
      "new_list.name_placeholder": "Enter List name",
      "new_list.create": "Create",
      added_confirmation: "Added to list",
      "message.success_ok": "Successfully added to list",
      "message.not_added": "Item was not added to list",
      "message.api_error": "Error adding to list",
      login_required: "Please sign in to save items to lists",
    };
    return strings[key] || key;
  };

  // Handle dropdown activation
  const activate = async () => {
    // Use cached authentication status from AuthContext
    if (!userId) {
      // Redirect to login if not authenticated
      sharedListApi.redirectToLogin();
      return;
    }

    setIsOpen(!isOpen);

    if (!isOpen) {
      // Force a list reload every time this is opened
      setReady(false);
      setLists([]);
      setMessage(null);

      try {
        const fetchedLists = await sharedListApi.getLists();
        setLists(fetchedLists);
        setReady(true);
      } catch (error) {
        setMessage("Error loading lists");
        setReady(true);
      }
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      setMessage(null);
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  // Create new list and add item (matching Angular master branch pattern)
  const createList = async () => {
    const listName = name ? name.trim() : "";

    if (listName !== "") {
      // Use cached authentication status from AuthContext
      if (!userId) {
        sharedListApi.redirectToLogin();
        return;
      }

      setReady(false);

      try {
        // Step 1: Create the list (matching Angular master branch)
        const newList = await sharedListApi.createList(listName);

        if (newList) {
          setLists((prevLists) => [...prevLists, newList]);

          // Step 2: Add item to the newly created list
          await addToList(newList);
        }

        setReady(true);
        setName("");
      } catch (error) {
        setMessage("Error creating list");
        setReady(true);
      }
    }
  };

  // Add product to selected list
  const addToList = async (list) => {
    if (!userId) {
      sharedListApi.redirectToLogin();
      return;
    }

    setReady(false);

    try {
      const result = await sharedListApi.addToList(list.listId, product, 1);

      if (result.message_key === "login_required") {
        // Handle login required case
        sharedListApi.redirectToLogin();
        return;
      }

      const messageText =
        result.message_key === "success_ok"
          ? `${getString(
              "added_confirmation"
            )} <a href="${sharedListApi.getListUrl(list)}">${list.listName}</a>`
          : getString(`message.${result.message_key}`);

      setMessage(messageText);
      setReady(true);

      // Track the add to list action (if tracking service is available)
      if (window.trackingService && window.trackingService.trackAddToList) {
        window.trackingService.trackAddToList([product]);
      }
    } catch (error) {
      setMessage("Error adding to list");
      setReady(true);
    }
  };

  // Check if user can create a list
  const canCreateList = () => {
    return name && name.trim() !== "";
  };

  // Handle space key in input (from Angular implementation)
  const handleKeyDown = (event) => {
    if (event.which === 32) {
      event.preventDefault();
      setName((prevName) => (prevName || "") + " ");
    }
  };

  // Render as link if not logged in, dropdown if logged in
  if (!userId) {
    return (
      <div className={className}>
        <div
          onClick={activate}
          className="atl-trigger"
          style={{ cursor: "pointer" }}
        >
          <span className="alt-dropdown-medium-padding">{label}</span>
        </div>
      </div>
    );
  }

  // Render full dropdown for logged in users
  return (
    <div
      className={`atl-dropdown-container ${
        isOpen ? "active" : ""
      } ${className}`}
      ref={containerRef}
    >
      <div onClick={activate} className="atl-trigger">
        <span className="alt-dropdown-medium-padding">{label}</span>
        <i className="tf-atl-arrow"></i>
      </div>

      {isOpen && (
        <div className="tf-atl-popup" onClick={(e) => e.stopPropagation()}>
          {ready ? (
            <div>
              {message == null ? (
                <div>
                  {lists.length < 1 && (
                    <div className="tf-text-center">
                      <div className="tf-atl-header">
                        {getString("new_list.create_first")}
                      </div>
                      <img
                        className="tf-atl-icon"
                        src="/assets/addToListIcon.png"
                        alt="Add to List"
                      />
                    </div>
                  )}

                  {lists.length > 0 && (
                    <ul>
                      {lists.map((list) => (
                        <li key={list.listId} onClick={() => addToList(list)}>
                          {list.listName}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="tf-atl-input">
                    <div className="tf-text-input-group">
                      <input
                        className="tf-text-input-value"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={getString("new_list.name_placeholder")}
                      />
                    </div>
                    <CoreButton
                      variant="primary"
                      className="tf-add-to-list-btn"
                      size="small"
                      type="button"
                      onClick={createList}
                      disabled={!canCreateList()}
                    >
                      <span slot="content">{getString("new_list.create")}</span>
                    </CoreButton>
                  </div>
                </div>
              ) : (
                <div className="tf-atl-message">
                  <i className="tf-ui-icon-check-tick"></i>
                  <span dangerouslySetInnerHTML={{ __html: message }}></span>
                </div>
              )}
            </div>
          ) : (
            <div className="tf-atl-loading">
              <CoreLoader
                variant="circular"
                functionality="indeterminate"
                size="small"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TfAddToList;
